import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { PanelContent, PanelPortal, PanelHeader } from '@frontend/panel-engine';
import { useBreakpoint } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { ModalControlModalProps, Tray, Heading, Button } from '@frontend/design-system';
import { VerifyInsuranceForm } from './verify-insurance-form';

interface Props extends ModalControlModalProps {
  hasVerificationError: boolean;
  shouldShowTuuthFairyReport: boolean;
  onReportLearnMoreClick: () => void;
  closeInsuranceSidePanel: () => void;
}

type VerifyInsuranceSidePanelProps = Omit<Props, 'closeInsuranceSidePanel'> & {
  // Set this true for a scenario where only the modal should be used instead of PanelEngine
  useModal?: boolean;
};

const VerifyInsurancePanelEngineDesktopView = ({
  onReportLearnMoreClick,
  closeInsuranceSidePanel,
  hasVerificationError,
  shouldShowTuuthFairyReport,
}: Props) => {
  const { t } = useTranslation('insuranceDetails');

  return (
    <PanelPortal id='weave-insurance-verification' width='smallXL' onClose={closeInsuranceSidePanel} enableAnimations>
      <PanelHeader css={headerStyles}>
        <PanelHeader.Title level={2}>{t('Verify Insurance')} </PanelHeader.Title>
        <PanelHeader.Close />
      </PanelHeader>
      <PanelContent css={panelContentCss}>
        <VerifyInsuranceForm
          hasVerificationError={hasVerificationError}
          onClose={closeInsuranceSidePanel}
          onReportLearnMoreClick={onReportLearnMoreClick}
          shouldShowTuuthFairyReport={shouldShowTuuthFairyReport}
        />
      </PanelContent>
    </PanelPortal>
  );
};
const VerifyInsuranceSideTrayMobileView = ({
  onReportLearnMoreClick,
  closeInsuranceSidePanel,
  shouldShowTuuthFairyReport,
  hasVerificationError,
  ...modalProps
}: Props) => {
  const { t } = useTranslation('insuranceDetails');
  return (
    <Tray {...modalProps} width='smallXL' css={{ padding: '0', gap: 0 }} disableCloseOnEscape>
      <Tray.Header
        css={headerStyles}
        Buttons={
          <Button iconName='x' variant='secondary' size='large' label='close' onClick={closeInsuranceSidePanel} />
        }
      >
        <Heading level={2}>{t('Verify Insurance')}</Heading>
      </Tray.Header>
      <Tray.Body css={panelContentCss}>
        <VerifyInsuranceForm
          hasVerificationError={hasVerificationError}
          onClose={closeInsuranceSidePanel}
          onReportLearnMoreClick={onReportLearnMoreClick}
          shouldShowTuuthFairyReport={shouldShowTuuthFairyReport}
        />
      </Tray.Body>
    </Tray>
  );
};

export const VerifyInsuranceSidePanel = ({
  shouldShowTuuthFairyReport,
  useModal = false,
  onReportLearnMoreClick,
  ...modalProps
}: VerifyInsuranceSidePanelProps) => {
  const size = useBreakpoint();
  const isDesktop = size === 'large' || size === 'xlarge';

  /**
   * When openModal is triggered from useModalControl then modalProps.show is set to true and sidePanel is displayed.
   * When onClose is triggered from useModalControl then modalProps.show is set to false and sidepanel is not displayed.
   */
  if (!modalProps.show) {
    return null;
  }
  if (isDesktop && !useModal) {
    return (
      <VerifyInsurancePanelEngineDesktopView
        {...modalProps}
        shouldShowTuuthFairyReport={shouldShowTuuthFairyReport}
        onReportLearnMoreClick={onReportLearnMoreClick}
        closeInsuranceSidePanel={modalProps.onClose}
      />
    );
  }
  return (
    <VerifyInsuranceSideTrayMobileView
      {...modalProps}
      shouldShowTuuthFairyReport={shouldShowTuuthFairyReport}
      onReportLearnMoreClick={onReportLearnMoreClick}
      closeInsuranceSidePanel={modalProps.onClose}
    />
  );
};

const headerStyles = css({
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.colors.neutral10}`,
});

const panelContentCss = css`
  padding: ${theme.spacing(3)};
  .tray-primary-button {
    margin-left: ${theme.spacing(2)};
  }
`;
